import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAdminAuth, formatApiError } from "@/admin/AdminAuthContext";
import { Helmet } from "react-helmet-async";
import { BRAND } from "@/lib/assets";

export default function AdminLogin() {
  const { login, user } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && !user.must_change_password) navigate("/admin", { replace: true });
    else if (user && user.must_change_password) navigate("/admin/change-password", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Signed in.");
      if (u.must_change_password) navigate("/admin/change-password", { replace: true });
      else navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Sign In — Salihi Group</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-navy-dark flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 justify-center mb-10">
            <img src={BRAND.logo}
                 alt="Salihi Group" className="h-12 brightness-0 invert" />
          </Link>

          <div className="bg-white p-8 md:p-10 shadow-2xl">
            <div className="flex items-center gap-3 mb-2">
              <span className="gold-hairline" />
              <span className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium">Admin</span>
            </div>
            <h1 className="font-serif text-navy text-3xl mb-2">Sign in</h1>
            <p className="text-mutedink text-sm mb-8">Enter your credentials to access the CMS.</p>

            <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
              <div>
                <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">Email</label>
                <input
                  type="email" required autoFocus autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email"
                  className="w-full border border-softgray px-4 py-3 text-navy outline-none focus:border-gold"
                />
              </div>
              <div>
                <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">Password</label>
                <input
                  type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  className="w-full border border-softgray px-4 py-3 text-navy outline-none focus:border-gold"
                />
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="admin-login-error">
                  {error}
                </div>
              )}
              <button
                type="submit" disabled={submitting}
                className="btn-gold w-full justify-center"
                data-testid="admin-login-submit"
              >
                {submitting ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
          <div className="text-center text-white/50 text-xs mt-6">
            <Link to="/" className="hover:text-gold">← Back to website</Link>
          </div>
        </div>
      </div>
    </>
  );
}
